import { useEffect, useRef, useState } from 'react'
import type { Vendeur } from '@troc/shared'
import { api, ErreurApi } from '../api'

/**
 * Ce qu'on peut faire sur son propre compte : changer son PIN, ou rendre la
 * caisse au suivant.
 *
 * Les PIN d'installation — 1234, 2345, 3456 — se devinent en trois essais
 * devant l'iPad. Tant qu'aucun écran ne permet d'en changer, personne ne le
 * fait ; c'est donc ici, à un doigt du portrait qu'on voit toute la journée.
 */
export function MonCompte({
  vendeur,
  onFermer,
  onDeconnecter,
  onFait,
}: {
  vendeur: Vendeur
  onFermer: () => void
  onDeconnecter: () => void
  /** Message de confirmation, affiché par l'écran appelant. */
  onFait: (message: string) => void
}) {
  const [actuel, setActuel] = useState('')
  const [nouveau, setNouveau] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const premier = useRef<HTMLInputElement>(null)

  useEffect(() => {
    premier.current?.focus()
  }, [])

  useEffect(() => {
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') onFermer()
    }
    window.addEventListener('keydown', auClavier)
    return () => window.removeEventListener('keydown', auClavier)
  }, [onFermer])

  const changer = async () => {
    if (envoi) return
    if (nouveau !== confirmation) {
      setErreur('Les deux nouveaux PIN ne sont pas identiques.')
      return
    }
    setEnvoi(true)
    setErreur(null)
    try {
      await api('/session/pin', { methode: 'POST', corps: { actuel, nouveau } })
      onFait(`PIN modifié — c’est celui-ci qu’il faudra taper désormais, ${vendeur.nom}.`)
      onFermer()
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Changement impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  const champPin = (
    id: string,
    valeur: string,
    changement: (v: string) => void,
    libelle: string,
    reference?: React.RefObject<HTMLInputElement | null>,
  ) => (
    <div className="champ">
      <label htmlFor={id}>{libelle}</label>
      <input
        id={id}
        ref={reference}
        className="saisie saisie-grande"
        type="password"
        inputMode="numeric"
        autoComplete="off"
        maxLength={4}
        value={valeur}
        onChange={(evenement) => {
          changement(evenement.target.value.replace(/\D/g, '').slice(0, 4))
          setErreur(null)
        }}
        onKeyDown={(evenement) => {
          if (evenement.key === 'Enter') void changer()
        }}
      />
    </div>
  )

  return (
    <div className="voile voile-centre" onMouseDown={onFermer}>
      <div
        className="fenetre-compte"
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-compte"
        onMouseDown={(evenement) => evenement.stopPropagation()}
      >
        <div className="fenetre-compte-entete">
          <span className="jeton">
            {vendeur.photo ? <img src={vendeur.photo} alt="" /> : vendeur.initiales}
          </span>
          <div>
            <h3 id="titre-compte">{vendeur.nom}</h3>
            <p className="sourdine">{vendeur.poste}</p>
          </div>
        </div>

        <div className="fenetre-compte-pin">
          <div className="etiquette">Changer mon PIN</div>
          {champPin('pin-actuel', actuel, setActuel, 'PIN actuel', premier)}
          {champPin('pin-nouveau', nouveau, setNouveau, 'Nouveau PIN')}
          {champPin('pin-confirmation', confirmation, setConfirmation, 'Le retaper')}
          {erreur && <p className="erreur-compte">{erreur}</p>}
          <button
            type="button"
            className="btn btn-primaire"
            disabled={envoi || actuel.length !== 4 || nouveau.length !== 4}
            onClick={() => void changer()}
          >
            {envoi ? 'Enregistrement…' : 'Enregistrer mon PIN'}
          </button>
        </div>

        <div className="fenetre-compte-pied">
          <button type="button" className="btn" onClick={onDeconnecter}>
            Fermer la session
          </button>
          <button type="button" className="btn btn-fantome" onClick={onFermer}>
            Retour à la caisse
          </button>
        </div>
      </div>
    </div>
  )
}
